import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ShieldCheck } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';

export default function ForceChangePassword() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast({ title: 'Error', description: 'New password must be at least 8 characters', variant: 'destructive' });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: 'Error', description: 'Passwords do not match', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      await api.changePassword(currentPassword, newPassword);
      toast({ title: 'Password Updated', description: 'Your password has been changed. Continuing to your dashboard.' });
      navigate('/admin');
    } catch (err) {
      toast({ title: 'Could not change password', description: err.message || 'Please try again', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="force-change-password-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-[#0A2C93]/10 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-[#0A2C93]" />
          </div>
          <h1 className="text-xl font-bold text-slate-900">Set a New Password</h1>
        </div>
        <p className="text-sm text-slate-500 mb-6">For security, you must change your temporary password before continuing.</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Current (temporary) password</label>
            <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#0A2C93]"
              data-testid="fcp-current-password" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">New password (min 8 chars)</label>
            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#0A2C93]"
              data-testid="fcp-new-password" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Confirm new password</label>
            <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#0A2C93]"
              data-testid="fcp-confirm-password" />
          </div>
          <button type="submit" disabled={loading}
            className="w-full py-3 rounded-xl bg-[#0A2C93] hover:bg-[#082273] disabled:bg-slate-300 text-white font-semibold transition flex items-center justify-center gap-2"
            data-testid="fcp-submit">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Change Password & Continue'}
          </button>
        </form>
      </div>
    </div>
  );
}
